// Settings → General: how the P&L works out cost of goods (F-RPT-N41).
// "From recipes and item costs" is the default. "From what you buy" is for a
// venue that records its purchases and costs nothing per item. Switching
// changes profit figures from the chosen date on, so it is admin-only and
// confirmed, and the server keeps the history.
//
// F-RPT-N42: on "From what you buy", the admin also says which of the venue's
// own categories are things it buys to sell. Those count as cost of goods from
// the chosen date, entries already recorded included — otherwise a food bill
// filed under "Meat" sits in operating expenses and the margin reads too high.
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { getStoredUserRole } from '@/lib/staff-roles'
import { useCurrency } from '@/contexts/CurrencyContext'
import type { CogsBasis, ExpenseCategoryDefinition } from '@/types'
import { useCogsBasis } from '@/components/admin/expenses/use-cogs-basis'
import { useExpenseCategoryDefs } from '@/components/admin/expenses/use-expense-category-defs'

const OPTIONS: { value: CogsBasis; title: string; body: string }[] = [
  {
    value: 'at_sale',
    title: 'From recipes and item costs',
    body: 'Each sale is charged the cost set on the item. Purchases are stock until they sell. Use this if you keep recipes or item costs up to date.',
  },
  {
    value: 'purchases',
    title: 'From what you buy',
    body: 'Everything recorded as a stock purchase is charged on the day it was bought. Use this if you do not set recipes or item costs.',
  },
]

const ERRORS: Record<string, string> = {
  cogs_basis_invalid: 'Choose one of the two options.',
  cogs_basis_effective_date_invalid: 'The start date must be after the last change and not in the future.',
  cogs_basis_unchanged: 'That option is already in use.',
  cogs_basis_consignment_not_supported:
    'This option is not available while you have consignment vendors. Their payouts would not be counted as a cost.',
  cogs_stock_categories_not_applicable: 'Categories can only be counted as stock on "From what you buy".',
  cogs_stock_category_invalid: 'One of the categories no longer exists. Reload and try again.',
}

/** The venue's own categories that are not stock yet — the ones worth asking about. */
function classifiable(defs: ExpenseCategoryDefinition[]): ExpenseCategoryDefinition[] {
  return defs
    .filter(d => d.is_active && !d.is_system && !d.is_inventory && d.slug !== 'inventory_purchase')
    .sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label))
}

function labelsFor(slugs: string[], defs: ExpenseCategoryDefinition[]): string {
  const byslug = new Map(defs.map(d => [d.slug, d.label]))
  return slugs.map(s => byslug.get(s) ?? s).join(', ')
}

export function CogsBasisSettingCard() {
  const isAdmin = getStoredUserRole() === 'admin'
  const { data: state } = useCogsBasis(isAdmin)
  const { data: defs = [] } = useExpenseCategoryDefs(isAdmin)
  const qc = useQueryClient()
  const { toast } = useToast()
  const { formatCurrency } = useCurrency()
  const [choice, setChoice] = useState<CogsBasis>('at_sale')
  const [from, setFrom] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [confirming, setConfirming] = useState(false)
  // "Count more categories as stock" on a tenant already on purchases.
  const [morePicked, setMorePicked] = useState<string[]>([])
  const [moreFrom, setMoreFrom] = useState('')
  const [confirmingMore, setConfirmingMore] = useState(false)

  // Sync from the server only when its basis or business date really changes.
  // Depending on the whole state object re-ran this on every background
  // refetch and wiped a choice the admin was still making.
  const serverBasis = state?.basis
  const serverToday = state?.today
  const serverFrom = state?.effective_from
  useEffect(() => {
    if (!serverBasis || !serverToday) return
    setChoice(serverBasis)
    setFrom(serverToday)
    setMoreFrom(serverFrom || serverToday)
  }, [serverBasis, serverToday, serverFrom])

  const candidates = useMemo(() => classifiable(defs), [defs])

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['cogsBasis'] })
    qc.invalidateQueries({ queryKey: ['expenseCategoryDefs'] })
    qc.invalidateQueries({ queryKey: ['pnl'] })
  }
  const fail = (e: Error) => toast({ title: 'Save failed', description: e.message, variant: 'destructive' })
  const unwrap = <T,>(res: { success: boolean; error?: string; message?: string; data?: T }) => {
    if (!res.success) throw new Error(ERRORS[res.error ?? ''] ?? res.message ?? 'Could not save')
    return res
  }

  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await apiClient.setCogsBasis({
          basis: choice,
          effective_from: from,
          ...(choice === 'purchases' && picked.length > 0 ? { stock_categories: picked } : {}),
        }),
      ),
    onSuccess: () => {
      setConfirming(false)
      setPicked([])
      invalidate()
      toast({ title: 'Cost basis updated', description: 'Reload any open reports to see the new figures.' })
    },
    onError: (e: Error) => {
      setConfirming(false)
      fail(e)
    },
  })

  const classify = useMutation({
    mutationFn: async () => unwrap(await apiClient.classifyStockCategories({ slugs: morePicked, from: moreFrom })),
    onSuccess: () => {
      setConfirmingMore(false)
      setMorePicked([])
      invalidate()
      toast({ title: 'Categories counted as stock', description: 'Reload any open reports to see the new figures.' })
    },
    onError: (e: Error) => {
      setConfirmingMore(false)
      fail(e)
    },
  })

  if (!isAdmin || !state) return null

  const lastChange = state.periods.length > 0 ? state.periods[state.periods.length - 1].effective_from : ''
  const dirty = choice !== state.basis
  const dateOk = from !== '' && from <= state.today && (lastChange === '' || from > lastChange)
  const backdated = from < state.today
  const askCategories = dirty && choice === 'purchases' && candidates.length > 0
  const onPurchases = !dirty && state.basis === 'purchases'
  const moreDateOk = moreFrom !== '' && moreFrom <= state.today && moreFrom >= (state.effective_from || '')

  const toggle = (list: string[], set: (v: string[]) => void, slug: string, on: boolean) =>
    set(on ? [...list, slug] : list.filter(s => s !== slug))

  // Switches and classifications, one list, oldest first.
  const history = [
    ...state.periods.map(p => ({
      at: p.set_at,
      text: `${p.effective_from} — ${p.basis === 'purchases' ? 'From what you buy' : 'From recipes and item costs'}${p.set_by_name ? ` · ${p.set_by_name}` : ''}`,
    })),
    ...(state.classifications ?? []).map(c => ({
      at: c.set_at,
      text: `${c.applied_from} — ${labelsFor(c.slugs, defs)} counted as stock (${c.rows_moved} ${c.rows_moved === 1 ? 'entry' : 'entries'}, ${formatCurrency(c.amount_moved)})${c.set_by_name ? ` · ${c.set_by_name}` : ''}`,
    })),
  ].sort((a, b) => a.at.localeCompare(b.at))

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Cost of goods</CardTitle>
        <CardDescription>How the profit and loss report works out what your sales cost you.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <fieldset className="space-y-3">
          <legend className="sr-only">Cost of goods method</legend>
          {OPTIONS.map((o) => (
            <label key={o.value} className="flex cursor-pointer items-start gap-3">
              <input
                type="radio"
                name="cogs-basis"
                className="mt-1 h-4 w-4"
                checked={choice === o.value}
                onChange={() => setChoice(o.value)}
              />
              <span className="space-y-0.5">
                <span className="block text-base font-medium">{o.title}</span>
                <span className="block text-sm text-muted-foreground">{o.body}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <p className="text-sm text-muted-foreground">
          Other screens and terminals show the new figures after they reload.
        </p>

        {dirty && (
          <div className="space-y-1.5">
            <Label htmlFor="cogs-basis-from">Start from</Label>
            <Input
              id="cogs-basis-from"
              type="date"
              className="h-11 w-48"
              value={from}
              max={state.today}
              onChange={(e) => setFrom(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              {dateOk
                ? 'Days before this date keep their current figures.'
                : 'Pick a date after the last change and not in the future.'}
            </p>
          </div>
        )}

        {askCategories && (
          <CategoryChecklist
            id="switch"
            title="Which of these are things you buy to sell?"
            help="Entries under the categories you tick count as cost of goods from the start date, including entries already recorded on or after it. Everything else stays an operating expense. Stock purchase already counts."
            candidates={candidates}
            picked={picked}
            onToggle={(slug, on) => toggle(picked, setPicked, slug, on)}
          />
        )}

        <Button className="h-11 px-6" disabled={!dirty || !dateOk || save.isPending} onClick={() => setConfirming(true)}>
          Save
        </Button>

        {onPurchases && candidates.length > 0 && (
          <div className="space-y-3 border-t pt-4">
            <CategoryChecklist
              id="more"
              title="Count more categories as stock"
              help="Tick the categories you buy to sell. Their entries from the date below count as cost of goods, including entries already recorded."
              candidates={candidates}
              picked={morePicked}
              onToggle={(slug, on) => toggle(morePicked, setMorePicked, slug, on)}
            />
            <div className="space-y-1.5">
              <Label htmlFor="cogs-stock-from">From</Label>
              <Input
                id="cogs-stock-from"
                type="date"
                className="h-11 w-48"
                value={moreFrom}
                min={state.effective_from || undefined}
                max={state.today}
                onChange={(e) => setMoreFrom(e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                {moreDateOk
                  ? 'Not before the day "From what you buy" began.'
                  : 'Pick a date on or after the start of "From what you buy" and not in the future.'}
              </p>
            </div>
            <Button
              variant="outline"
              className="h-11 px-6"
              disabled={morePicked.length === 0 || !moreDateOk || classify.isPending}
              onClick={() => setConfirmingMore(true)}
            >
              Count as stock
            </Button>
          </div>
        )}

        {history.length > 0 && (
          <div className="border-t pt-4">
            <p className="mb-2 text-sm font-medium">Past changes</p>
            <ul className="space-y-1 text-sm text-muted-foreground">
              {[...history].reverse().map((h, i) => (
                <li key={`${h.at}-${i}`} className="tabular-nums">
                  {h.text}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change how cost of goods is worked out?</DialogTitle>
            <DialogDescription>
              {backdated
                ? `Profit for every day from ${from} to today will be recalculated. Earlier days stay as they are.`
                : `Profit from today (${from}) onward will use the new method. Earlier days stay as they are.`}{' '}
              {choice === 'purchases' && picked.length > 0
                ? `${picked.length} ${picked.length === 1 ? 'category' : 'categories'} (${labelsFor(picked, defs)}) will count as stock from ${from}, including entries already recorded on or after that date. `
                : ''}
              This cannot be undone, only changed again from a later date.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="h-11" onClick={() => setConfirming(false)}>
              Keep current
            </Button>
            <Button className="h-11" disabled={save.isPending} onClick={() => save.mutate()}>
              Change cost basis
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmingMore} onOpenChange={setConfirmingMore}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Count these categories as stock?</DialogTitle>
            <DialogDescription>
              {labelsFor(morePicked, defs)} will count as cost of goods from {moreFrom}, including entries already recorded on or
              after that date. Net profit does not change; the split between cost of goods and operating expenses does. This
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="h-11" onClick={() => setConfirmingMore(false)}>
              Cancel
            </Button>
            <Button className="h-11" disabled={classify.isPending} onClick={() => classify.mutate()}>
              Count as stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

function CategoryChecklist({
  id,
  title,
  help,
  candidates,
  picked,
  onToggle,
}: {
  id: string
  title: string
  help: string
  candidates: ExpenseCategoryDefinition[]
  picked: string[]
  onToggle: (slug: string, on: boolean) => void
}) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">{title}</legend>
      <p className="text-sm text-muted-foreground">{help}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {candidates.map(d => {
          const cid = `cogs-stock-${id}-${d.slug}`
          return (
            <label key={d.slug} htmlFor={cid} className="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm">
              <Checkbox id={cid} checked={picked.includes(d.slug)} onCheckedChange={(v) => onToggle(d.slug, v === true)} />
              <span>{d.label}</span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}
